import { Grid, TextField, Typography } from '@mui/material'
import type { DeliveryAddress } from '@/api/types'

export const BLANK_DELIVERY_ADDRESS: DeliveryAddress = {
  doorNo: '',
  buildingName: '',
  floorNo: '',
  streetName: '',
  areaName: '',
  postalCode: '',
  landmark: '',
}

/**
 * Where the food goes, taken in the parts an address is written in.
 *
 * <p>One free text box is fine for a hall the office already knows and useless
 * for a house nobody has been to. A driver looking for a reception at eight in
 * the evening needs the door number, the floor and something to recognise the
 * turning by, and a line typed in a hurry rarely carries all three.</p>
 *
 * <p>Nothing here is required: the customer may only know the area when they
 * first ring, and the rest is filled in as it is learnt.</p>
 */
export function DeliveryAddressFields({
  value,
  onChange,
  disabled,
}: {
  value: DeliveryAddress
  onChange: (patch: Partial<DeliveryAddress>) => void
  disabled?: boolean
}) {
  const field = (
    key: keyof DeliveryAddress,
    label: string,
    span: number,
    extra?: { helperText?: string; maxLength?: number; inputMode?: 'numeric' },
  ) => (
    <Grid item xs={12} sm={span}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value[key] ?? ''}
        disabled={disabled}
        onChange={(event) => onChange({ [key]: event.target.value })}
        helperText={extra?.helperText}
        inputProps={{
          maxLength: extra?.maxLength,
          inputMode: extra?.inputMode,
          'aria-label': label,
        }}
      />
    </Grid>
  )

  const pincode = value.postalCode ?? ''
  const pincodeWrong = pincode.length > 0 && !/^[1-9][0-9]{5}$/.test(pincode)

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Delivery address
        </Typography>
      </Grid>
      {field('doorNo', 'Door no', 3)}
      {field('buildingName', 'Apartment / house name', 5)}
      {field('floorNo', 'Floor', 4, { helperText: 'Ground, 2nd, 3rd left' })}
      {field('streetName', 'Street name', 6)}
      {field('areaName', 'Area', 6)}
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label="Pin code"
          value={pincode}
          disabled={disabled}
          onChange={(event) =>
            onChange({ postalCode: event.target.value.replace(/\D/g, '').slice(0, 6) })
          }
          error={pincodeWrong}
          helperText={pincodeWrong ? 'Six digits' : ' '}
          inputProps={{ inputMode: 'numeric', maxLength: 6, 'aria-label': 'Pin code' }}
        />
      </Grid>
      {field('landmark', 'Landmark', 8, { helperText: 'What the driver turns at' })}
    </>
  )
}
